import {
  CircularProgress,
  SxProps,
  Checkbox,
  Table as MuiTable,
  TableBody as MuiMuiTableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Typography,
} from '@mui/material'
import React, { useCallback, useState } from 'react'
import styles from './styles'

export interface ITableColumn<T = any> {
  title: React.ReactNode
  dataIndex?: string | null //  data key
  render?: (col: ITableColumn & { _index: number }, data: T) => React.ReactNode
  sx?: SxProps
}
export type ITableProps = {
  columns: ITableColumn[]
  datas?: any[]
  sx?: SxProps
  loading?: boolean
  checkBox?: boolean
  showHead?: boolean
  isDashboard?: boolean
  onRowClick?: (ev: any, item: any, key: number) => void
}

const Table: React.FC<ITableProps> = ({
  loading,
  columns,
  datas,
  sx,
  onRowClick,
  checkBox = false,
  showHead = true,
}) => {
  const [selected, setSelected] = useState<number[]>([])

  const onSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked && datas) {
        const newSelected = datas.map((item, index) => index)
        setSelected(newSelected)
        return
      }
      setSelected([])
    },
    [datas],
  )

  const onSelectSingleClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, key: number) => {
      if (event.target.checked) {
        if (!selected.includes(key)) {
          setSelected([...selected, key])
        }
      } else {
        const existIndex = selected.findIndex((value) => value === key)
        selected.splice(existIndex, 1)
        setSelected([...selected])
      }
    },
    [selected],
  )

  return (
    <TableContainer sx={sx}>
      <MuiTable sx={styles.table}>
        {showHead && (
          <MuiTableHead sx={styles.head}>
            <MuiTableRow>
              {checkBox && (
                <MuiTableCell padding="checkbox">
                  <Checkbox color="success" onChange={onSelectAllClick} />
                </MuiTableCell>
              )}

              {columns.map((col, index) => (
                <MuiTableCell key={index} sx={{ ...col.sx, border: 0 }}>
                  <Typography variant="subtitle1">{col.title}</Typography>
                </MuiTableCell>
              ))}
            </MuiTableRow>
          </MuiTableHead>
        )}

        <MuiMuiTableBody sx={styles.body}>
          {!datas && loading ? (
            <MuiTableRow>
              <MuiTableCell colSpan={checkBox ? columns.length + 1 : columns.length} sx={{ textAlign: 'center' }}>
                <CircularProgress color="inherit" />
              </MuiTableCell>
            </MuiTableRow>
          ) : datas && datas?.length > 0 ? (
            datas?.map((data, key) => (
              <MuiTableRow
                key={key}
                onClick={(ev) => {
                  onRowClick?.(ev, data, key)
                }}
              >
                {checkBox && (
                  <MuiTableCell padding="checkbox">
                    <Checkbox
                      color="success"
                      checked={selected.includes(key)}
                      onChange={(e) => onSelectSingleClick(e, key)}
                    />
                  </MuiTableCell>
                )}
                {columns.map((col, index) => {
                  if (col?.render) {
                    return (
                      <MuiTableCell
                        key={index + key}
                        sx={{ ...col.sx, borderBottom: '1px solid rgba(191, 255, 11, 0.2)' }}
                      >
                        {col?.render ? col.render?.({ ...col, _index: key }, data) : null}
                      </MuiTableCell>
                    )
                  }

                  return (
                    <MuiTableCell key={index + key} sx={{ ...col.sx }}>
                      {col.dataIndex ? data[col.dataIndex] : ''}
                    </MuiTableCell>
                  )
                })}
              </MuiTableRow>
            ))
          ) : (
            <MuiTableRow>
              <MuiTableCell colSpan={checkBox ? columns.length + 1 : columns.length} sx={{ textAlign: 'center' }}>
                No Record
              </MuiTableCell>
            </MuiTableRow>
          )}
        </MuiMuiTableBody>
      </MuiTable>
    </TableContainer>
  )
}

export default Table
